import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../api/client";
import LoadingSpinner from "../components/LoadingSpinner";

const COMPLIANCE_CHECKS = [
    { key: "chair", label: "Chair" },
    { key: "earbuds", label: "Earbuds" },
    { key: "doors", label: "Doors" },
    { key: "uniform", label: "Uniform" },
    { key: "name_badge", label: "Badge" },
];

const STATUS_COLORS = {
    not_onboarded: { bg: "#3a3a3a", text: "#888" },
    failed: { bg: "rgba(239,68,68,0.25)", text: "#ef4444" },
    awaiting_review: { bg: "rgba(59,130,246,0.25)", text: "#3b82f6" },
    reviewed: { bg: "rgba(34,197,94,0.2)", text: "#22c55e" },
    no_scan: { bg: "transparent", text: "var(--border)" },
};

function Monitoring() {
    const navigate = useNavigate();
    const [view, setView] = useState("heatmap"); // heatmap | fixture
    const [period, setPeriod] = useState("day");
    const [date, setDate] = useState(() => new Date().toLocaleDateString('en-CA', { timeZone: 'Australia/Melbourne' }));
    const [stateFilter, setStateFilter] = useState("");
    const [rplFilter, setRplFilter] = useState("");
    const [search, setSearch] = useState("");
    const [hideNonOnboarded, setHideNonOnboarded] = useState(false);

    const [filters, setFilters] = useState({ states: [], rpls: [] });
    const [heatmapData, setHeatmapData] = useState(null);
    const [fixtureData, setFixtureData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Load filter options
    useEffect(() => {
        api.monitoringFilters().then(setFilters).catch(() => {});
    }, []);

    const loadData = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            if (view === "heatmap") {
                const data = await api.monitoringHeatmap({
                    period, date, state: stateFilter, rpl: rplFilter,
                    search, hideNonOnboarded
                });
                setHeatmapData(data);
            } else {
                const data = await api.monitoringFixtureDetail({
                    date, state: stateFilter, rpl: rplFilter, search
                });
                setFixtureData(data);
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [view, period, date, stateFilter, rplFilter, search, hideNonOnboarded]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    // Group sites by state
    const groupByState = (sites) => {
        const groups = {};
        for (const site of sites) {
            const key = site.stateCode || "Unknown";
            if (!groups[key]) groups[key] = [];
            groups[key].push(site);
        }
        return Object.entries(groups).sort((a, b) => a[0].localeCompare(b[0]));
    };

    return (
        <div>
            <div className="page-header">
                <h2>Monitoring</h2>
            </div>

            {/* View + Period toggles + Filters */}
            <div style={{ padding: "0 20px 12px", display: "flex", flexWrap: "wrap", gap: 12, alignItems: "center" }}>
                {/* View toggle */}
                <div style={{ display: "flex", gap: 0 }}>
                    {[
                        { key: "heatmap", label: "Scan Heatmap" },
                        { key: "fixture", label: "Fixture Detail" },
                    ].map((v, i, arr) => (
                        <button
                            key={v.key}
                            onClick={() => setView(v.key)}
                            style={{
                                padding: "7px 16px", fontSize: 12, fontWeight: 600, cursor: "pointer",
                                border: "1px solid var(--border)",
                                borderRight: i < arr.length - 1 ? "none" : undefined,
                                borderRadius: i === 0 ? "6px 0 0 6px" : "0 6px 6px 0",
                                background: view === v.key ? "var(--primary)" : "var(--bg-hover)",
                                color: view === v.key ? "#fff" : "var(--text-secondary)",
                            }}
                        >{v.label}</button>
                    ))}
                </div>

                {/* Period toggle (heatmap only) */}
                {view === "heatmap" && (
                    <div style={{ display: "flex", gap: 0 }}>
                        {["day", "week", "month"].map((p, i, arr) => (
                            <button
                                key={p}
                                onClick={() => setPeriod(p)}
                                style={{
                                    padding: "7px 14px", fontSize: 12, fontWeight: 600, cursor: "pointer",
                                    border: "1px solid var(--border)",
                                    borderRight: i < arr.length - 1 ? "none" : undefined,
                                    borderRadius: i === 0 ? "6px 0 0 6px" : i === arr.length - 1 ? "0 6px 6px 0" : "0",
                                    background: period === p ? "var(--primary)" : "var(--bg-hover)",
                                    color: period === p ? "#fff" : "var(--text-secondary)",
                                    textTransform: "capitalize",
                                }}
                            >{p}</button>
                        ))}
                    </div>
                )}

                {/* Date picker */}
                <input
                    type="date"
                    className="filter-select"
                    value={date}
                    onChange={(e) => setDate(e.target.value)}
                    style={{ fontSize: 12 }}
                />

                {/* State filter */}
                <select className="filter-select" value={stateFilter} onChange={(e) => setStateFilter(e.target.value)} style={{ fontSize: 12 }}>
                    <option value="">All States</option>
                    {filters.states.map(s => <option key={s.code} value={s.code}>{s.code}</option>)}
                </select>

                {/* RPL filter */}
                <select className="filter-select" value={rplFilter} onChange={(e) => setRplFilter(e.target.value)} style={{ fontSize: 12 }}>
                    <option value="">All RPLs</option>
                    {filters.rpls.map(r => <option key={r} value={r}>{r}</option>)}
                </select>

                {/* Search */}
                <input
                    type="text"
                    className="filter-select"
                    placeholder="Search site..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    style={{ fontSize: 12, minWidth: 140 }}
                />

                {/* Hide non-onboarded */}
                {view === "heatmap" && (
                    <label style={{ fontSize: 12, color: "var(--text-secondary)", display: "flex", alignItems: "center", gap: 4, cursor: "pointer" }}>
                        <input type="checkbox" checked={hideNonOnboarded} onChange={(e) => setHideNonOnboarded(e.target.checked)} />
                        Hide non-onboarded
                    </label>
                )}
            </div>

            {/* Legend (heatmap only) */}
            {view === "heatmap" && (
                <div style={{ padding: "0 20px 12px", display: "flex", gap: 16, fontSize: 11, color: "var(--text-secondary)" }}>
                    {[
                        { status: "reviewed", label: "Reviewed" },
                        { status: "awaiting_review", label: "Awaiting Review" },
                        { status: "failed", label: "Failed" },
                        { status: "not_onboarded", label: "Not Onboarded" },
                        { status: "no_scan", label: "No Scan" },
                    ].map(l => (
                        <div key={l.status} style={{ display: "flex", alignItems: "center", gap: 4 }}>
                            <span style={{
                                width: 12, height: 12, borderRadius: 2, display: "inline-block",
                                background: STATUS_COLORS[l.status].bg,
                                border: l.status === "no_scan" ? "1px solid var(--border)" : "none",
                            }} />
                            {l.label}
                        </div>
                    ))}
                </div>
            )}

            {/* Content */}
            {loading ? (
                <LoadingSpinner message="Loading monitoring data..." />
            ) : error ? (
                <div className="empty-state"><p style={{ color: "var(--red)" }}>{error}</p></div>
            ) : view === "heatmap" ? (
                <HeatmapGrid data={heatmapData} period={period} navigate={navigate} groupByState={groupByState} />
            ) : (
                <FixtureDetailGrid data={fixtureData} groupByState={groupByState} navigate={navigate} />
            )}
        </div>
    );
}

function HeatmapGrid({ data, period, navigate, groupByState }) {
    if (!data || !data.sites || data.sites.length === 0) {
        return <div className="empty-state"><p>No sites to display.</p></div>;
    }

    const grouped = groupByState(data.sites);

    return (
        <div style={{ padding: "0 20px 20px", overflowX: "auto" }}>
            <table style={{ borderCollapse: "collapse", width: "100%", fontSize: 11 }}>
                <thead>
                    <tr>
                        <th style={{
                            position: "sticky", left: 0, zIndex: 2,
                            background: "var(--bg-primary)", padding: "6px 10px",
                            textAlign: "left", borderBottom: "2px solid var(--border)",
                            minWidth: 180
                        }}>Site</th>
                        {data.slots.map(s => (
                            <th key={s.key} style={{
                                padding: "6px 2px", textAlign: "center",
                                borderBottom: "2px solid var(--border)",
                                fontWeight: 500, color: "var(--text-secondary)",
                                minWidth: period === "day" ? 32 : 50,
                                whiteSpace: "nowrap"
                            }}>{s.label}</th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {grouped.map(([stateCode, sites]) => (
                        <React.Fragment key={stateCode}>
                            <tr>
                                <td colSpan={data.slots.length + 1} style={{
                                    padding: "8px 10px 4px", fontWeight: 700, fontSize: 12,
                                    color: "var(--text-primary)", background: "var(--bg-secondary)",
                                    borderBottom: "1px solid var(--border)"
                                }}>
                                    {stateCode}
                                    <span style={{ fontWeight: 400, color: "var(--text-muted)", marginLeft: 6 }}>
                                        ({sites.length})
                                    </span>
                                </td>
                            </tr>
                            {sites.map(site => (
                                <tr key={site.siteId}>
                                    <td style={{
                                        position: "sticky", left: 0, zIndex: 1,
                                        background: "var(--bg-primary)", padding: "4px 10px",
                                        borderBottom: "1px solid var(--border)",
                                        whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                                        maxWidth: 200
                                    }}>
                                        <span style={{ fontWeight: 600 }}>{site.name}</span>
                                        <span style={{ color: "var(--text-muted)", marginLeft: 4 }}>#{site.siteId}</span>
                                    </td>
                                    {site.cells.map((cell, i) => {
                                        const colors = STATUS_COLORS[cell.status] || STATUS_COLORS.no_scan;
                                        const clickable = cell.status === "awaiting_review" || cell.status === "reviewed";
                                        return (
                                            <td
                                                key={i}
                                                onClick={clickable ? () => {
                                                    // Build scanHour from slot
                                                    const hourParam = period === "day"
                                                        ? `${new Date().toISOString().slice(0, 10)}+${String(cell.slot).padStart(2, "0")}`
                                                        : `${cell.slot}+12`; // default to noon for day-level views
                                                    navigate(`/reviews/${site.siteId}/${encodeURIComponent(hourParam)}`);
                                                } : undefined}
                                                title={
                                                    cell.status === "reviewed" ? `Reviewed (${cell.issueCount || 0} issues, score: ${cell.avgScore || '-'})` :
                                                    cell.status === "awaiting_review" ? `Awaiting review (score: ${cell.avgScore || '-'})` :
                                                    cell.status === "failed" ? "Scan failed" :
                                                    cell.status === "not_onboarded" ? "Not onboarded" : "No scan"
                                                }
                                                style={{
                                                    padding: 2, textAlign: "center",
                                                    borderBottom: "1px solid var(--border)",
                                                    cursor: clickable ? "pointer" : "default"
                                                }}
                                            >
                                                <div style={{
                                                    background: colors.bg,
                                                    color: colors.text,
                                                    borderRadius: 3,
                                                    padding: "3px 2px",
                                                    fontSize: 10,
                                                    fontWeight: 600,
                                                    minHeight: 18,
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    border: cell.status === "no_scan" ? "1px solid var(--border)" : "none",
                                                }}>
                                                    {cell.status === "reviewed" && cell.issueCount > 0 ? cell.issueCount : ""}
                                                </div>
                                            </td>
                                        );
                                    })}
                                </tr>
                            ))}
                        </React.Fragment>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

function FixtureDetailGrid({ data, groupByState, navigate }) {
    if (!data || !data.rows || data.rows.length === 0) {
        return <div className="empty-state"><p>No fixture data to display.</p></div>;
    }

    // Group rows by state
    const grouped = groupByState(data.rows);

    const formatScanHour = (scanHour, tz) => {
        try {
            const d = new Date(scanHour.replace(" ", "T") + ":00:00Z");
            return d.toLocaleString("en-AU", {
                hour: "numeric", hour12: true, timeZone: tz || "Australia/Melbourne"
            }).toLowerCase();
        } catch { return scanHour.slice(11) + ":00"; }
    };

    // Track which site was shown on the previous row to avoid repeating names
    let lastSiteId = null;

    return (
        <div style={{ padding: "0 20px 20px", overflowX: "auto" }}>
            {/* Legend */}
            <div style={{ display: "flex", gap: 16, fontSize: 11, color: "var(--text-secondary)", marginBottom: 10 }}>
                {[
                    { bg: "rgba(34,197,94,0.2)", border: "none", label: "Reviewed (no issues)" },
                    { bg: "rgba(249,115,22,0.2)", border: "none", label: "Reviewed (issues raised)" },
                    { bg: "rgba(59,130,246,0.25)", border: "none", label: "Not Reviewed" },
                    { bg: "transparent", border: "1px solid var(--border)", label: "No Preset" },
                ].map(l => (
                    <div key={l.label} style={{ display: "flex", alignItems: "center", gap: 4 }}>
                        <span style={{ width: 12, height: 12, borderRadius: 2, display: "inline-block", background: l.bg, border: l.border }} />
                        {l.label}
                    </div>
                ))}
            </div>
            <table style={{ borderCollapse: "collapse", width: "100%", fontSize: 11 }}>
                <thead>
                    <tr>
                        <th style={{
                            position: "sticky", left: 0, zIndex: 2,
                            background: "var(--bg-primary)", padding: "6px 10px",
                            textAlign: "left", borderBottom: "2px solid var(--border)",
                            minWidth: 160
                        }}>Site</th>
                        <th style={{
                            padding: "6px 6px", textAlign: "center",
                            borderBottom: "2px solid var(--border)",
                            fontWeight: 500, color: "var(--text-secondary)", minWidth: 50
                        }}>Time</th>
                        {data.fixtureTypes.map(ft => (
                            <th key={ft} style={{
                                padding: "4px 2px", textAlign: "center",
                                borderBottom: "2px solid var(--border)",
                                fontWeight: 500, color: "var(--text-secondary)",
                                width: 32, minWidth: 32, maxWidth: 32,
                                verticalAlign: "bottom", height: 80
                            }}>
                                <div style={{
                                    writingMode: "vertical-rl", transform: "rotate(180deg)",
                                    whiteSpace: "nowrap", fontSize: 10, lineHeight: "32px"
                                }}>{ft.replace(/_/g, " ")}</div>
                            </th>
                        ))}
                        {COMPLIANCE_CHECKS.map(c => (
                            <th key={c.key} style={{
                                padding: "4px 2px", textAlign: "center",
                                borderBottom: "2px solid var(--border)",
                                fontWeight: 500, color: "#a78bfa",
                                width: 32, minWidth: 32, maxWidth: 32,
                                verticalAlign: "bottom", height: 80
                            }}>
                                <div style={{
                                    writingMode: "vertical-rl", transform: "rotate(180deg)",
                                    whiteSpace: "nowrap", fontSize: 10, lineHeight: "32px"
                                }}>{c.label}</div>
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {grouped.map(([stateCode, rows]) => {
                        lastSiteId = null;
                        return (
                        <React.Fragment key={stateCode}>
                            <tr>
                                <td colSpan={data.fixtureTypes.length + COMPLIANCE_CHECKS.length + 2} style={{
                                    padding: "8px 10px 4px", fontWeight: 700, fontSize: 12,
                                    color: "var(--text-primary)", background: "var(--bg-secondary)",
                                    borderBottom: "1px solid var(--border)"
                                }}>
                                    {stateCode}
                                    <span style={{ fontWeight: 400, color: "var(--text-muted)", marginLeft: 6 }}>
                                        ({[...new Set(rows.map(r => r.siteId))].length} sites, {rows.length} scans)
                                    </span>
                                </td>
                            </tr>
                            {rows.map((row, idx) => {
                                const showSite = row.siteId !== lastSiteId;
                                lastSiteId = row.siteId;
                                const scanHourParam = row.scanHour.replace(" ", "+");
                                const clickable = row.fixtureCells && Object.values(row.fixtureCells).some(v => v !== "blank");
                                return (
                                    <tr key={`${row.siteId}:${row.scanHour}`}>
                                        <td style={{
                                            position: "sticky", left: 0, zIndex: 1,
                                            background: "var(--bg-primary)", padding: "4px 10px",
                                            borderBottom: "1px solid var(--border)",
                                            whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                                            maxWidth: 180
                                        }}>
                                            {showSite ? (
                                                <>
                                                    <span style={{ fontWeight: 600 }}>{row.name}</span>
                                                    <span style={{ color: "var(--text-muted)", marginLeft: 4 }}>#{row.siteId}</span>
                                                </>
                                            ) : null}
                                        </td>
                                        <td style={{
                                            padding: "4px 6px", textAlign: "center",
                                            borderBottom: "1px solid var(--border)",
                                            fontWeight: 600, color: "var(--text-secondary)", fontSize: 10
                                        }}>
                                            {formatScanHour(row.scanHour, row.timezone)}
                                        </td>
                                        {data.fixtureTypes.map(ft => {
                                            const cell = row.fixtureCells[ft] || { status: "blank", issues: 0 };
                                            const status = cell.status || "blank";
                                            const isReviewed = status === "reviewed";
                                            const isScanned = status === "not_reviewed";
                                            return (
                                                <td
                                                    key={ft}
                                                    onClick={clickable ? () => navigate(`/reviews/${row.siteId}/${encodeURIComponent(scanHourParam)}`) : undefined}
                                                    style={{
                                                        padding: 2, textAlign: "center",
                                                        borderBottom: "1px solid var(--border)",
                                                        cursor: clickable ? "pointer" : "default"
                                                    }}
                                                >
                                                    <div style={{
                                                        borderRadius: 3, padding: "3px 2px",
                                                        fontSize: 10, fontWeight: 600,
                                                        minHeight: 18, display: "flex",
                                                        alignItems: "center", justifyContent: "center",
                                                        background: isReviewed
                                                            ? (cell.issues > 0 ? "rgba(249,115,22,0.2)" : "rgba(34,197,94,0.2)")
                                                            : isScanned ? "rgba(59,130,246,0.25)"
                                                            : "transparent",
                                                        color: isReviewed
                                                            ? (cell.issues > 0 ? "#f97316" : "#22c55e")
                                                            : isScanned ? "#3b82f6"
                                                            : "var(--border)",
                                                        border: status === "blank" ? "1px solid var(--border)" : "none",
                                                    }}>
                                                        {isReviewed ? (cell.issues > 0 ? cell.issues : "") : ""}
                                                    </div>
                                                </td>
                                            );
                                        })}
                                        {COMPLIANCE_CHECKS.map(c => {
                                            const val = row.compliance[c.key];
                                            const isYes = val === true;
                                            const isNo = val === false;
                                            return (
                                                <td key={c.key} style={{
                                                    padding: 2, textAlign: "center",
                                                    borderBottom: "1px solid var(--border)",
                                                }}>
                                                    <div style={{
                                                        borderRadius: 3, padding: "3px 2px",
                                                        fontSize: 10, fontWeight: 600,
                                                        minHeight: 18, display: "flex",
                                                        alignItems: "center", justifyContent: "center",
                                                        background: isYes ? "rgba(34,197,94,0.2)"
                                                            : isNo ? "rgba(239,68,68,0.2)"
                                                            : "transparent",
                                                        color: isYes ? "#22c55e"
                                                            : isNo ? "#ef4444"
                                                            : "var(--border)",
                                                        border: !isYes && !isNo ? "1px solid var(--border)" : "none",
                                                    }}>
                                                        {isYes ? "Y" : isNo ? "N" : ""}
                                                    </div>
                                                </td>
                                            );
                                        })}
                                    </tr>
                                );
                            })}
                        </React.Fragment>
                    );})}
                </tbody>
            </table>
        </div>
    );
}

export default Monitoring;
